
import React, { useState, useEffect, useRef } from 'react';
import { createChatSession } from '../services/geminiService';
import { api } from '../services/api';
import { Icons } from '../constants';
import { Chat, GenerateContentResponse } from "@google/genai";
import { RevenueAnomaly } from '../types';

interface Message {
  id: string;
  role: 'user' | 'model';
  text: string;
}

const AIConsultant: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [anomalies, setAnomalies] = useState<RevenueAnomaly[]>([]);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      role: 'model',
      text: "Growth Assistant online. Click start to begin analysis."
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const chatSessionRef = useRef<Chat | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchAnomalies = async () => {
      try {
        const data = await api.analytics.getAnomalies();
        if (data && Array.isArray(data)) {
          setAnomalies(data);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchAnomalies();
  }, []);

  const initializeAI = async () => {
    setIsInitializing(true);
    setInitError(null);
    try {
      let metrics, mrrData, customers;
      let retries = 4;
      
      while (retries > 0) {
        try {
          [metrics, mrrData, customers] = await Promise.all([
            api.analytics.getDashboard(),
            api.analytics.getMrrMovement(),
            api.customers.getAll()
          ]);
          break;
        } catch (err) {
          retries--;
          if (retries === 0) throw err;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      chatSessionRef.current = createChatSession(metrics, mrrData, customers);
      setIsInitialized(true);
      setMessages(prev => [...prev, {
        id: 'ready',
        role: 'model',
        text: "Growth Assistant ready. I've detected 3 unusual patterns in your revenue. How should we proceed?"
      }]);
    } catch (err) {
      console.error("Failed to init AI context", err);
      setInitError("Failed to start Assistant. Please check your connection and try again.");
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const handleSend = async (text: string = input) => {
    if (!text.trim() || !chatSessionRef.current) return;

    const userMsg: Message = { id: Date.now().toString(), role: 'user', text: text };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    try {
      const result = await chatSessionRef.current.sendMessageStream({ message: text });
      const aiMsgId = (Date.now() + 1).toString();
      setMessages(prev => [...prev, { id: aiMsgId, role: 'model', text: '' }]);

      let fullText = '';
      for await (const chunk of result) {
        const chunkText = (chunk as GenerateContentResponse).text || '';
        fullText += chunkText;
        setMessages(prev => prev.map(msg => msg.id === aiMsgId ? { ...msg, text: fullText } : msg));
      }
    } catch (error) {
      console.error("Chat Error:", error);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className="h-[calc(100vh-140px)] flex flex-col xl:flex-row gap-6 animate-in slide-in-from-bottom-4 duration-500">
      {!isInitialized ? (
        <div className="flex-1 flex flex-col items-center justify-center bg-white dark:bg-slate-800 rounded-[3rem] border border-slate-200 dark:border-slate-700 shadow-sm p-8 text-center">
          {initError && (
            <div className="text-red-500 font-bold mb-4 bg-red-50 dark:bg-red-900/20 p-4 rounded-xl">{initError}</div>
          )}
          <button 
            onClick={initializeAI}
            disabled={isInitializing}
            className="px-10 py-5 bg-indigo-600 text-white rounded-[2rem] font-black text-sm uppercase tracking-widest hover:bg-indigo-700 transition-all disabled:opacity-50 shadow-xl"
          >
            {isInitializing ? 'Starting...' : 'Start Assistant'}
          </button>
        </div>
      ) : (
        <>
          {/* Intelligence Sidebar */}
          <div className="w-full xl:w-80 space-y-6 hidden xl:block">
            <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white h-full border border-white/5 relative overflow-hidden">
               <h2 className="text-xl font-black mb-6 uppercase tracking-tight">Active Anomalies</h2>
               <div className="space-y-4">
                  {anomalies.map(anom => (
                    <div key={anom.id} onClick={() => handleSend(`Analyze anomaly ${anom.id}: ${anom.title}`)} className="p-4 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all cursor-pointer group">
                       <div className="flex justify-between items-start mb-2">
                          <div className={`w-2 h-2 rounded-full ${anom.severity === 'Critical' ? 'bg-red-500 animate-pulse' : 'bg-amber-500'}`}></div>
                          <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest">{anom.type.replace('_', ' ')}</span>
                       </div>
                       <p className="text-[11px] font-bold text-slate-300 leading-tight">{anom.title}</p>
                    </div>
                  ))}
               </div>
               <div className="mt-12 pt-12 border-t border-white/10">
                  <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-4">Recommended Actions</p>
                  <button onClick={() => handleSend("Draft a churn recovery email for German customers")} className="w-full text-left py-2 text-xs font-bold text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                     <div className="w-1.5 h-1.5 bg-slate-700 rounded-full"></div>
                     Draft Recovery Email
                  </button>
                  <button onClick={() => handleSend("Why did contraction MRR increase in the Starter tier?")} className="w-full text-left py-2 text-xs font-bold text-slate-400 hover:text-white transition-colors flex items-center gap-2">
                     <div className="w-1.5 h-1.5 bg-slate-700 rounded-full"></div>
                     Analyze Contraction MRR
                  </button>
               </div>
               <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px]"></div>
            </div>
          </div>

          {/* Main Assistant Chat */}
          <div className="flex-1 bg-white dark:bg-slate-800 rounded-[3rem] border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden flex flex-col relative group">
            <div className="flex-1 overflow-y-auto p-8 space-y-8 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex gap-6 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'model' && (
                    <div className="w-10 h-10 rounded-2xl bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0 shadow-inner">
                      <Icons.AI />
                    </div>
                  )}
                  <div className={`max-w-[75%] rounded-[2rem] p-6 text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user' 
                      ? 'bg-slate-900 text-white rounded-br-none shadow-xl' 
                      : 'bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-slate-200 rounded-bl-none border border-slate-100 dark:border-slate-700 shadow-sm'
                  }`}>
                    {msg.text}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-10 h-10 rounded-2xl bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-500 shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    </div>
                  )}
                </div>
              ))}
              {isTyping && (
                <div className="flex gap-6 justify-start">
                   <div className="w-10 h-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400">
                      <div className="animate-pulse"><Icons.AI /></div>
                   </div>
                   <div className="bg-slate-50 dark:bg-slate-900/30 rounded-full px-6 py-4 flex gap-1.5 items-center border border-slate-100 dark:border-slate-800 shadow-sm">
                      <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-100"></div>
                      <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce delay-200"></div>
                   </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="p-6 bg-white dark:bg-slate-800 border-t border-slate-100 dark:border-slate-700">
              <div className="relative max-w-4xl mx-auto flex gap-4">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                  disabled={isInitializing}
                  placeholder="Query growth trends, anomaly root-causes, or draft customer strategy..."
                  className="flex-1 px-8 py-5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-[2rem] outline-none focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 font-bold text-slate-900 dark:text-white transition-all shadow-inner"
                />
                <button 
                  onClick={() => handleSend()}
                  disabled={!input.trim() || isTyping || isInitializing}
                  className="px-10 bg-indigo-600 text-white rounded-[2rem] font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all disabled:opacity-50 shadow-xl shadow-indigo-100 dark:shadow-none"
                >
                  Analyze
                </button>
              </div>
              <p className="text-center text-[9px] text-slate-400 font-black uppercase tracking-[0.25em] mt-4 opacity-50">Smart Assistant Active • Gemini 3 Pro Reasoning Engine</p>
            </div>
            {/* Visual flair: Terminal corner decoration */}
            <div className="absolute top-4 right-4 flex gap-1.5 opacity-20">
               <div className="w-2 h-2 rounded-full bg-slate-400"></div>
               <div className="w-2 h-2 rounded-full bg-slate-400"></div>
               <div className="w-2 h-2 rounded-full bg-slate-400"></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AIConsultant;
